/**
 * AreaLocationFormDialog — New / Edit / Copy cho 1 dòng public.factory_area
 * (Master data card Area/Location trong Factory Data hub).
 *
 * KHÔNG có tài liệu Access form (factory_area chỉ là reference table dùng làm
 * RowSource) → form build mới theo convention web:
 *   - full_area_code: required (strCheck).
 *   - square_m2: số căn phải (Format Standard).
 *   - stock (Warehouse): Autocomplete freeSolo, gợi ý từ các stock đã có của
 *     factory hiện tại (không cần endpoint riêng — derive từ list).
 * Mode Edit/Copy hydrate trực tiếp từ row đã có trên list (không fetch API) nên
 * render field ngay, không cần loading spinner.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

import type { FactoryAreaPayload, FactoryAreaRow } from '@/store/api/factoryApi'
import { FORM_GRID_2COL, TEXT_TRUNCATE } from '@/theme/responsive'

export type AreaLocationDialogMode = 'new' | 'edit' | 'copy'

/** Full Area Code = key duy nhất TUYỆT ĐỐI. Chuẩn hoá để so trùng
 *  case-insensitive + trim (dùng chung page ↔ dialog). */
export const normalizeFullCode = (full: string | null): string =>
  (full ?? '').trim().toLowerCase()

interface AreaLocationFormDialogProps {
  open: boolean
  mode: AreaLocationDialogMode
  saving: boolean
  canEdit: boolean
  factoryCode: string
  initial: FactoryAreaRow | null
  stockOptions: string[]
  /** Full Area Code (chuẩn hoá) của các record đang có (exclude row đang Edit) —
   *  để check trùng ngay trên form (New/Copy). Full code là key duy nhất tuyệt đối. */
  existingFullCodes: string[]
  onClose: () => void
  onSave: (payload: FactoryAreaPayload) => void
}

interface Draft {
  full_area_code: string
  area_code: string
  stock: string
  square_m2: string
  note: string
}

const EMPTY_DRAFT: Draft = {
  full_area_code: '',
  area_code: '',
  stock: '',
  square_m2: '',
  note: '',
}

const titleBtnSx = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

const compactInputSx = {
  '& .MuiInputBase-root': { minHeight: 30, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.4, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
}

// Readonly bg standard (skill /convert-form) — key fields khi Edit.
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': { color: 'text.secondary', fontWeight: 600 },
}

const draftFromRow = (row: FactoryAreaRow | null): Draft => {
  if (!row) return EMPTY_DRAFT
  return {
    full_area_code: row.full_area_code ?? '',
    area_code: row.area_code ?? '',
    stock: row.stock ?? '',
    square_m2: row.square_m2 == null ? '' : String(row.square_m2),
    note: row.note ?? '',
  }
}

export default function AreaLocationFormDialog({
  open,
  mode,
  saving,
  canEdit,
  factoryCode,
  initial,
  stockOptions,
  existingFullCodes,
  onClose,
  onSave,
}: AreaLocationFormDialogProps) {
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    // New → blank; Edit/Copy → hydrate từ row (Copy tái dùng field, tạo record mới).
    setDraft(mode === 'new' ? EMPTY_DRAFT : draftFromRow(initial))
    setError('')
  }, [open, mode, initial])

  const setField = (key: keyof Draft, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  // Full Area Code + Area Code = key định danh → readonly khi Edit (không đổi key).
  const keyReadonly = mode === 'edit'
  const existingFullCodeSet = useMemo(() => new Set(existingFullCodes), [existingFullCodes])
  // Check trùng Full Area Code (duy nhất tuyệt đối) ngay trên form (New/Copy) —
  // bỏ qua khi Edit vì key readonly.
  const isDuplicate = useMemo(() => {
    if (keyReadonly) return false
    if (!draft.full_area_code.trim()) return false
    return existingFullCodeSet.has(normalizeFullCode(draft.full_area_code))
  }, [keyReadonly, draft.full_area_code, existingFullCodeSet])

  const headerLabel = useMemo(() => {
    const verb = mode === 'new' ? 'New' : mode === 'copy' ? 'Copy' : 'Edit'
    const code = draft.full_area_code.trim()
    return code ? `${verb} Area/Location — ${code}` : `${verb} Area/Location`
  }, [mode, draft.full_area_code])

  const handleSave = () => {
    if (!canEdit || saving) return
    const full = draft.full_area_code.trim()
    if (!full) {
      setError('Full Area Code is required')
      return
    }
    if (isDuplicate) {
      setError('This Full Area Code already exists')
      return
    }
    let square_m2: number | null = null
    const sq = draft.square_m2.trim()
    if (sq !== '') {
      const n = Number(sq)
      if (!Number.isFinite(n)) {
        setError('Square m² must be a number')
        return
      }
      square_m2 = n
    }
    setError('')
    onSave({
      full_area_code: full,
      area_code: draft.area_code.trim() || null,
      stock: draft.stock.trim() || null,
      square_m2,
      note: draft.note.trim() || null,
    })
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(640px, 92vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{ pb: 1, pt: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}
      >
        <Typography
          component="span"
          noWrap
          title={headerLabel}
          sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          {headerLabel}
        </Typography>
        <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          {factoryCode.toUpperCase()}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={handleSave}
            disabled={!canEdit || saving || isDuplicate}
            sx={titleBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button variant="outlined" size="small" onClick={onClose} disabled={saving} sx={titleBtnSx}>
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{ ...compactInputSx, overflowY: 'auto', flex: '1 1 auto', minHeight: 0, pt: 1.5 }}
      >
        {error ? (
          <Alert severity="error" sx={{ mb: 1.25, py: 0.25 }}>
            {error}
          </Alert>
        ) : null}

        <Box sx={FORM_GRID_2COL}>
          <TextField
            label="Full Area Code"
            size="small"
            fullWidth
            required
            value={draft.full_area_code}
            onChange={(e) => setField('full_area_code', e.target.value)}
            disabled={!canEdit || saving || keyReadonly}
            InputProps={keyReadonly ? { readOnly: true } : undefined}
            error={isDuplicate}
            helperText={
              keyReadonly
                ? 'Key field — cannot be changed'
                : isDuplicate
                  ? 'Full Area Code already exists'
                  : ' '
            }
            sx={keyReadonly ? readonlyFieldSx : undefined}
            autoFocus={!keyReadonly}
          />
          <TextField
            label="Area Code"
            size="small"
            fullWidth
            value={draft.area_code}
            onChange={(e) => setField('area_code', e.target.value)}
            disabled={!canEdit || saving || keyReadonly}
            InputProps={keyReadonly ? { readOnly: true } : undefined}
            helperText={keyReadonly ? 'Key field — cannot be changed' : ' '}
            sx={keyReadonly ? readonlyFieldSx : undefined}
          />
          <Autocomplete
            freeSolo
            size="small"
            options={stockOptions}
            value={draft.stock}
            onChange={(_e, v) => setField('stock', v ?? '')}
            onInputChange={(_e, v) => setField('stock', v)}
            disabled={!canEdit || saving}
            renderInput={(params) => (
              <TextField {...params} label="Warehouse (Stock)" size="small" fullWidth />
            )}
          />
          <TextField
            label="Square m²"
            size="small"
            fullWidth
            type="number"
            value={draft.square_m2}
            onChange={(e) => setField('square_m2', e.target.value)}
            disabled={!canEdit || saving}
            inputProps={{ style: { textAlign: 'right' } }}
          />
          <TextField
            label="Note"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={draft.note}
            onChange={(e) => setField('note', e.target.value)}
            disabled={!canEdit || saving}
            sx={{ gridColumn: '1 / -1' }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
